import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, ExternalLink, AlertOctagon } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { MerchantLogo } from "../components/MerchantLogo";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmt2 = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const CADENCE_LABEL = {
    en: { monthly: "monthly", biweekly: "biweekly", quarterly: "quarterly" },
    es: { monthly: "mensual", biweekly: "quincenal", quarterly: "trimestral" },
};

const SubRow = ({ s, isEs }) => {
    const next = s.next_charge ? new Date(s.next_charge) : null;
    const daysAway = next ? Math.max(0, Math.round((next - new Date()) / (1000 * 60 * 60 * 24))) : null;
    const slug = s.merchant.toLowerCase().replace(/\s+/g, "-");
    return (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 md:p-5 flex items-center gap-4"
             data-testid={`sub-row-${slug}`}>
            <MerchantLogo merchant={s.merchant} size={48} />
            <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base md:text-lg truncate">{s.merchant}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {fmt2(s.amount)} · {CADENCE_LABEL[isEs ? "es" : "en"][s.cadence] || s.cadence}
                    {daysAway !== null && (
                        <> · {isEs ? "próximo en" : "next in"} {daysAway} {isEs ? "días" : "days"}</>
                    )}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {isEs ? "Cuesta al año" : "Costs per year"}:{" "}
                    <span className="font-bold text-[hsl(var(--foreground))]">{fmt(s.annual_cost)}</span>
                </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
                {s.cancel_url ? (
                    <a
                        href={s.cancel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`cancel-link-${slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 transition-colors"
                    >
                        <X className="w-3.5 h-3.5" />
                        {isEs ? "Cancelar" : "Cancel"}
                        <ExternalLink className="w-3 h-3 opacity-70" />
                    </a>
                ) : (
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-bold text-right max-w-[130px] leading-tight">
                        {isEs ? "Cancela en el sitio del comercio" : "Cancel in the merchant's site"}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function Subscriptions() {
    const { language } = useApp();
    const isEs = language === "es";
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/planning/subscriptions")
            .then((r) => setData(r.data))
            .catch(() => setData({ subscriptions: [], monthly_total: 0, annual_total: 0, count: 0 }));
    }, []);

    return (
        <AppLayout title={t(language, "nav_subscriptions")}>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                data-testid="subscriptions-page"
            >
                {/* Hero summary */}
                <div className="rounded-3xl border border-red-500/40 p-6 md:p-8 mb-6"
                     style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.06) 100%)" }}>
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-red-500/25 text-red-500 grid place-items-center shrink-0">
                            <AlertOctagon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs uppercase tracking-widest font-bold text-red-400/90 mb-1">
                                {isEs ? "Cazador de suscripciones" : "Subscription Killer"}
                            </div>
                            <div className="font-display font-black text-3xl md:text-5xl leading-tight">
                                {data ? fmt(data.annual_total) : "…"}
                                <span className="text-base md:text-lg font-medium text-[hsl(var(--muted-foreground))] ml-2">
                                    {isEs ? "al año en total" : "per year total"}
                                </span>
                            </div>
                            <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                                {data
                                    ? `${data.count} ${isEs ? "suscripciones detectadas" : "subscriptions detected"} · ${fmt(data.monthly_total)}/${isEs ? "mes" : "mo"}`
                                    : (isEs ? "Analizando tus cargos recurrentes…" : "Analyzing your recurring charges…")}
                            </div>
                            <div className="mt-3 text-sm leading-snug">
                                {isEs
                                    ? "La suscripción promedio se olvida a los 4 meses. Revisa y cancela cualquiera que no estés usando."
                                    : "The average subscription is forgotten by month 4. Review and cancel anything you're not using."}
                            </div>
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="space-y-3">
                    {data === null && (
                        <div className="text-center text-[hsl(var(--muted-foreground))] py-12">
                            {isEs ? "Cargando…" : "Loading…"}
                        </div>
                    )}
                    {data && data.subscriptions.length === 0 && (
                        <div className="text-[hsl(var(--muted-foreground))] text-sm py-10 text-center border border-dashed border-[hsl(var(--border))] rounded-xl"
                             data-testid="subs-empty">
                            {isEs
                                ? "Aún no detectamos suscripciones. Conecta tu banco y regresa en 1-2 meses."
                                : "No subscriptions detected yet. Connect your bank and check back in 1-2 months."}
                        </div>
                    )}
                    {data?.subscriptions.map((s) => (
                        <SubRow key={s.merchant} s={s} isEs={isEs} />
                    ))}
                </div>
            </motion.div>
        </AppLayout>
    );
}
